/**
 * network-api.ts — API helpers for Circles, RFQ, and Tenant Groups
 */

import { api } from './api';

// ─── Types ──────────────────────────────────────────

export type CircleType = 'ownership' | 'agent_network' | 'vendor_panel' | 'peer_circle' | 'consortium';

export interface Circle {
  id: string;
  name: string;
  circle_type: CircleType;
  description: string | null;
  status: 'active' | 'archived' | 'suspended';
  owner_tenant_id: string;
  config: Record<string, unknown>;
  created_at: string;
  member_role: string;
  member_count: number;
}

export interface CircleMember {
  id: string;
  circle_id: string;
  tenant_id: string | null;
  external_name: string | null;
  member_role: 'owner' | 'admin' | 'affiliate' | 'member' | 'observer' | 'suspended';
  country_code: string | null;
  capabilities: string[];
  service_areas: string[];
  status: 'active' | 'invited' | 'left' | 'removed';
  joined_at: string | null;
  tenant_name?: string;
  tenant_slug?: string;
  tenant_status?: string;
  tenant_entity_type?: string;
  tenant_default_currency?: string;
  tenant_timezone?: string;
}

export interface CircleDetail extends Circle {
  members: CircleMember[];
  commercialSummary?: GroupCommercialSummaryResponse | null;
}

export interface RfqSummary {
  id: string;
  circle_id: string | null;
  creator_tenant_id: string;
  title: string;
  rfq_type: 'spot' | 'contract' | 'recurring';
  rfq_mode: 'open' | 'sealed' | 'mixed';
  currency: string;
  deadline: string | null;
  status: 'draft' | 'published' | 'quoting' | 'evaluating' | 'awarded' | 'completed' | 'cancelled' | 'expired';
  created_at: string;
  _role: 'creator' | 'invitee';
  circle_name?: string;
}

export interface RfqDetail extends RfqSummary {
  description: string | null;
  cargo_summary: Record<string, unknown> | null;
  requirements: Record<string, unknown> | null;
  valid_until: string | null;
  awarded_at: string | null;
  invitees?: RfqInvitee[];
  quotes?: RfqQuote[];
}

export interface RfqInvitee {
  id: string;
  tenant_id: string | null;
  external_name: string | null;
  member_role: 'trusted' | 'bidder' | 'observer';
  status: string;
  invited_at: string;
  tenant_name?: string;
}

export interface RfqQuote {
  id: string;
  invitee_id: string;
  unit_price: number | null;
  total_price: number | null;
  currency: string;
  quantity: number | null;
  validity_days: number | null;
  terms: string | null;
  notes: string | null;
  is_awarded: boolean;
  status: string;
  submitted_at: string | null;
  created_at: string;
}

export interface TenantGroup {
  id: string;
  slug: string;
  name: string;
  status: string;
  config: Record<string, unknown>;
  created_at: string;
  my_role: string;
  tenant_count: number;
}

export interface GroupMember {
  id: string;
  user_id: string;
  role: string;
  email: string;
  name: string | null;
  created_at: string;
}

export interface GroupTenant {
  id: string;
  slug: string;
  name: string;
  entity_type: string;
  country_code: string | null;
  status: string;
  default_currency?: string;
  timezone?: string;
}

export interface GroupCommercialTotals {
  groupTenantCount: number;
  readyTenantCount: number;
  activeTenantCount: number;
  attentionTenantCount: number;
  openDeals: number;
  overdueFollowUps: number;
  staleDeals: number;
  openQuotations: number;
  activeJobs: number;
  activeUsers: number;
  totalUsers: number;
  jobsToday: number;
  jobsMonthToDate: number;
  billedToday: number;
  unresolvedExceptions: number;
}

export interface GroupCommercialTenantSummary {
  tenantId: string;
  slug: string;
  name: string;
  status: string | null;
  countryCode: string | null;
  entityType: string | null;
  defaultCurrency: string | null;
  timezone: string | null;
  healthStatus: 'steady' | 'active' | 'attention';
  openDeals: number;
  activePipelineValue: number;
  weightedPipelineValue: number;
  overdueFollowUps: number;
  staleDeals: number;
  openQuotations: number;
  activeJobs: number;
  activeUsers: number;
  totalUsers: number;
  jobsToday: number;
  jobsMonthToDate: number;
  billedToday: number;
  unresolvedExceptions: number;
  lastActivityAt: string | null;
  nextFollowUpAt: string | null;
  topRecommendationCustomerId: string | null;
  topRecommendationCustomerName: string | null;
  topRecommendationTitle: string | null;
  dataStatus: 'ready' | 'unavailable';
  dataMessage?: string;
}

export interface GroupCommercialSummaryResponse {
  group: {
    id: string;
    slug: string;
    name: string;
    status: string;
  };
  generatedAt: string;
  totals: GroupCommercialTotals;
  tenants: GroupCommercialTenantSummary[];
}

// ─── Group Inter-Company / Audit / Finance Rollup (Phase 4) ─────────────────

export interface GroupInterCompanyFlow {
  fromTenantId: string;
  fromTenantName: string;
  fromTenantSlug: string;
  toTenantId: string;
  toTenantName: string;
  toTenantSlug: string;
  jobCount: number;
  withChargeCode: number;
  missingChargeCode: number;
  topChargeCodes: Array<{ code: string; count: number }>;
}

export interface GroupInterCompanyTenantSummary {
  tenantId: string;
  slug: string;
  name: string;
  outboundJobs: number;
  inboundJobs: number;
  reciprocityRatio: number;
}

export interface GroupInterCompanyResponse {
  group: { id: string; slug: string; name: string; status: string };
  generatedAt: string;
  totals: { interCompanyJobs: number; pairsWithFlow: number; missingChargeCode: number };
  flows: GroupInterCompanyFlow[];
  tenantSummaries: GroupInterCompanyTenantSummary[];
}

export type GroupAuditSeverity = 'info' | 'warning' | 'critical';
export type GroupAuditRule = 'R1_INTERCO_MISSING_CHARGE' | 'R2_VOLUME_DROP' | 'R3_AR_OVERDUE_60D';

export interface GroupAuditFinding {
  id: string;
  rule: GroupAuditRule;
  severity: GroupAuditSeverity;
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  title: string;
  detail: string;
  metric?: { label: string; value: number | string };
}

export interface GroupAuditResponse {
  group: { id: string; slug: string; name: string; status: string };
  generatedAt: string;
  totals: { critical: number; warning: number; info: number };
  findings: GroupAuditFinding[];
}

export interface GroupFinanceRollupTenantRow {
  tenantId: string;
  slug: string;
  name: string;
  currency: string;
  countryCode: string | null;
  entityType: string | null;
  arPosted: number;
  arOpen: number;
  arOverdue30: number;
  arOverdue60: number;
  arOverdue90: number;
  apOpen: number;
  jobsThisMonth: number;
  jobsLastMonth: number;
  dataStatus: 'ready' | 'unavailable';
  dataMessage?: string;
}

export interface GroupFinanceRollupResponse {
  group: { id: string; slug: string; name: string; status: string };
  generatedAt: string;
  period: { monthStart: string; today: string };
  currencyBreakdown: Array<{
    currency: string;
    arPosted: number;
    arOpen: number;
    arOverdue60: number;
    apOpen: number;
    tenantCount: number;
  }>;
  tenants: GroupFinanceRollupTenantRow[];
}

// ─── Circle APIs ────────────────────────────────────

export const circleApi = {
  list: () => api.get<Circle[]>('/network/circles'),
  get: (id: string, options?: { tenantIds?: string[] }) => api.get<CircleDetail>(`/network/circles/${id}`, { tenantId: options?.tenantIds }),
  create: (body: { name: string; circle_type: CircleType; description?: string; config?: Record<string, unknown> }) =>
    api.post<Circle>('/network/circles', body),
  update: (id: string, body: Partial<{ name: string; description: string; config: Record<string, unknown>; status: string }>) =>
    api.patch<Circle>(`/network/circles/${id}`, body),
  addMember: (id: string, body: { tenant_id?: string; external_name?: string; member_role?: string; capabilities?: string[]; service_areas?: string[] }) =>
    api.post<CircleMember>(`/network/circles/${id}/members`, body),
  removeMember: (id: string, mid: string) =>
    api.delete(`/network/circles/${id}/members/${mid}`),
};

// ─── RFQ APIs ────────────────────────────────────────

export const rfqApi = {
  list: () => api.get<RfqSummary[]>('/network/rfq'),
  get: (id: string) => api.get<RfqDetail>(`/network/rfq/${id}`),
  create: (body: {
    circle_id?: string;
    title: string;
    description?: string;
    rfq_type?: string;
    rfq_mode?: string;
    cargo_summary?: Record<string, unknown>;
    requirements?: Record<string, unknown>;
    currency?: string;
    deadline?: string;
  }) => api.post<RfqDetail>('/network/rfq', body),
  publish: (id: string) => api.post<RfqDetail>(`/network/rfq/${id}/publish`, {}),
  cancel: (id: string) => api.post<{ id: string; status: string }>(`/network/rfq/${id}/cancel`, {}),
  addInvitee: (id: string, body: { tenant_id?: string; external_name?: string; member_role?: string }) =>
    api.post<RfqInvitee>(`/network/rfq/${id}/invitees`, body),
  getQuotes: (id: string) => api.get<RfqQuote[]>(`/network/rfq/${id}/quotes`),
  submitQuote: (id: string, body: { unit_price?: number; total_price?: number; quantity?: number; validity_days?: number; terms?: string; notes?: string }) =>
    api.post<RfqQuote>(`/network/rfq/${id}/quotes`, body),
  award: (id: string, awards: Array<{ invitee_id: string; quantity?: number }>) =>
    api.post<{ awarded: number }>(`/network/rfq/${id}/award`, { awards }),
};

// ─── Group APIs ──────────────────────────────────────

export const groupApi = {
  list: () => api.get<TenantGroup[]>('/groups'),
  get: (id: string, options?: { tenantIds?: string[] }) =>
    api.get<TenantGroup & { tenants: GroupTenant[]; members?: GroupMember[] }>(`/groups/${id}`, { tenantId: options?.tenantIds }),
  getCommercialSummary: (id: string, options?: { tenantIds?: string[] }) =>
    api.get<GroupCommercialSummaryResponse>(`/groups/${id}/commercial-summary`, { tenantId: options?.tenantIds }),
  getInterCompanyFlows: (id: string) => api.get<GroupInterCompanyResponse>(`/groups/${id}/inter-company-flows`),
  getAuditFindings: (id: string) => api.get<GroupAuditResponse>(`/groups/${id}/audit-findings`),
  getFinanceRollup: (id: string) => api.get<GroupFinanceRollupResponse>(`/groups/${id}/finance/rollup`),
  getTenants: (id: string) => api.get<GroupTenant[]>(`/groups/${id}/tenants`),
  switchTenant: (groupId: string, tenantId: string) =>
    api.post<{ accessToken: string; refreshToken: string; tenant: { id: string; name: string } }>(
      `/groups/${groupId}/switch/${tenantId}`, {}
    ),
};

// ─── Boox Network — Directory, Connect, Messaging ───────────────────────────

export interface NetworkMember {
  id: string;
  company_name: string;
  company_code: string | null;
  country_code: string | null;
  industry: string | null;
  tagline: string | null;
  description: string | null;
  status: 'active' | 'suspended' | 'pending';
  capabilities: string[];
  service_types: string[];
  operating_regions: string[];
  verification_status: 'unverified' | 'partial' | 'fully_verified';
  joined_at: string;
  contact_email?: string | null;
  contact_phone?: string | null;
  connection_status?: 'active' | 'pending_sent' | 'pending_received' | 'none';
}

export interface NetworkConnection {
  id: string;
  from_member_id: string;
  to_member_id: string;
  status: 'pending' | 'active' | 'declined' | 'revoked';
  connection_type: 'partner' | 'vendor' | 'customer' | 'agent';
  introduction_message: string | null;
  created_at: string;
  responded_at: string | null;
  // Joined from network_members
  peer_member?: NetworkMember;
}

export interface NetworkDmMessage {
  id: string;
  from_member_id: string;
  to_member_id: string;
  content: string;
  message_type: string;
  subject: string | null;
  attachments: Record<string, unknown>[] | null;
  is_read: boolean;
  created_at: string;
}

export interface NetworkInboxThread {
  peer_member_id: string;
  peer_company_name: string;
  last_message: string;
  last_message_at: string;
  unread_count: number;
}

export interface NetworkChannel {
  id: string;
  name: string;
  slug: string | null;
  description: string | null;
  channel_type: 'trade_lane' | 'port' | 'cargo_type' | 'regulation' | 'company' | 'group';
  visibility: 'public' | 'verified_only' | 'invite' | 'contract';
  member_count: number;
  post_count: number;
  regions: string[];
  tags: string[];
  is_subscribed: boolean;
  created_at: string;
}

export interface ChannelPost {
  id: string;
  channel_id: string;
  member_id: string;
  post_type: string;
  title: string | null;
  body: string;
  attachments: Record<string, unknown>[] | null;
  reaction_count: number;
  reply_count: number;
  created_at: string;
  author_name: string;
}

export interface AllianceOverview {
  contracts: number;
  active_contracts: number;
  member_count: number;
  monthly_teu: number;
  open_actions: number;
  recent_conversations: number;
  my_role?: string;
}

export interface AllianceContract {
  id: string;
  circle_id: string;
  contract_code: string;
  contract_name: string;
  contract_kind: 'master_rate' | 'named_account' | 'bundle_account' | 'space_commitment' | 'service_agreement' | 'rebate_program';
  counterparty_type: 'carrier' | 'forwarder' | 'agent' | 'vendor' | 'warehouse' | 'trucker' | 'other';
  counterparty_name: string;
  owner_tenant_id: string;
  status: 'draft' | 'active' | 'expired' | 'suspended' | 'archived';
  effective_from: string | null;
  effective_to: string | null;
  trade_lanes: Record<string, unknown>[];
  service_scope: Record<string, unknown>;
  pricing_terms: Record<string, unknown>;
  commercial_terms: Record<string, unknown>;
  source_reference: Record<string, unknown>;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface AllianceContractEntitlement {
  id: string;
  contract_id: string;
  circle_id: string;
  member_id: string;
  entitlement_type: 'lane_access' | 'named_account_access' | 'bundle_account_access' | 'space_allocation' | 'rebate_share' | 'pricing_visibility';
  access_level: 'view' | 'use' | 'propose_override' | 'manage';
  lane_scope: Record<string, unknown>;
  account_scope: Record<string, unknown>;
  capacity_terms: Record<string, unknown>;
  pricing_overlay: Record<string, unknown>;
  status: 'active' | 'suspended' | 'expired' | 'revoked';
  effective_from: string | null;
  effective_to: string | null;
  member_tenant_id?: string | null;
  member_external_name?: string | null;
  member_role?: string | null;
  member_status?: string | null;
  created_at: string;
}

export interface AllianceVolumeContribution {
  id: string;
  circle_id: string;
  member_id: string;
  contribution_period: string;
  mode: 'ocean' | 'air' | 'road' | 'rail' | 'multimodal';
  direction: 'import' | 'export' | 'cross_trade' | 'domestic' | 'unknown';
  pol_code: string | null;
  pod_code: string | null;
  carrier_name: string | null;
  named_account: string | null;
  teu: number;
  cbm: number;
  shipment_count: number;
  revenue_amount: number;
  cost_amount: number;
  source_type: 'manual' | 'email_intake' | 'api' | 'erp_sync' | 'ai_estimate';
  source_reference: Record<string, unknown>;
  created_at: string;
}

export interface AllianceActionRequest {
  id: string;
  circle_id: string;
  contract_id: string | null;
  requested_by_member_id: string | null;
  assigned_to_member_id: string | null;
  action_type: 'allocation_request' | 'rate_override' | 'capacity_release' | 'partner_onboarding' | 'exception_approval' | 'contract_activation';
  priority: 'low' | 'normal' | 'high' | 'critical';
  status: 'open' | 'in_review' | 'approved' | 'rejected' | 'cancelled' | 'completed';
  requested_payload: Record<string, unknown>;
  decision_payload: Record<string, unknown>;
  evidence_pack: Record<string, unknown>[];
  due_at: string | null;
  decided_at: string | null;
  created_at: string;
  updated_at: string;
  approval_request_id?: string | null;
  approval_status?: 'pending' | 'approved' | 'rejected' | 'expired' | 'escalated' | null;
  approval_timeout_at?: string | null;
  my_unread_notification_count?: number;
  has_unread_notification?: boolean;
  sla_state?: 'none' | 'on_track' | 'due_soon' | 'overdue' | 'closed';
  hours_to_due?: number | null;
  is_overdue?: boolean;
}

export interface AllianceConversationLedgerEntry {
  id: string;
  circle_id: string;
  related_object_type: 'contract' | 'entitlement' | 'volume' | 'action_request' | 'shipment' | 'rfq' | 'other';
  related_object_id: string | null;
  source_type: 'official_email' | 'network_dm' | 'network_channel' | 'topic_thread' | 'system_note';
  direction: 'inbound' | 'outbound' | 'internal';
  subject: string | null;
  summary: string | null;
  source_ref: Record<string, unknown>;
  participant_refs: Record<string, unknown>[];
  evidence_pack: Record<string, unknown>[];
  authored_by_tenant_id: string | null;
  created_at: string;
}

function qs(params?: Record<string, string | number | boolean | undefined>): string {
  if (!params) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined) p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const networkDirectoryApi = {
  list: (params?: { q?: string; capability?: string; region?: string; verified?: boolean; page?: number; limit?: number }) =>
    api.get<{ items: NetworkMember[]; total: number; page: number; limit: number }>(`/network/directory${qs(params)}`),
  me: () => api.get<NetworkMember>('/network/directory/me'),
  getMember: (memberId: string) => api.get<NetworkMember>(`/network/directory/${memberId}`),
  updateMe: (body: Partial<Pick<NetworkMember, 'tagline' | 'description' | 'service_types' | 'operating_regions' | 'capabilities' | 'contact_email' | 'contact_phone'>>) =>
    api.patch<NetworkMember>('/network/directory/me', body),
};

export const networkConnectApi = {
  list: (params?: { status?: string; page?: number; limit?: number }) =>
    api.get<{ items: NetworkConnection[]; total: number }>(`/network/connect${qs(params)}`),
  pending: () => api.get<{ items: NetworkConnection[]; total: number }>('/network/connect/pending'),
  send: (body: { to_member_id: string; connection_type?: string; introduction_message?: string }) =>
    api.post<NetworkConnection>('/network/connect', body),
  accept: (id: string) => api.post<NetworkConnection>(`/network/connect/${id}/accept`, {}),
  decline: (id: string) => api.post<NetworkConnection>(`/network/connect/${id}/decline`, {}),
  revoke: (id: string) => api.delete(`/network/connect/${id}`),
};

export const networkMessageApi = {
  inbox: () => api.get<{ items: NetworkInboxThread[]; total: number }>('/network/messages'),
  thread: (memberId: string, params?: { page?: number; limit?: number }) =>
    api.get<{ items: NetworkDmMessage[]; total: number }>(`/network/messages/${memberId}${qs(params)}`),
  send: (memberId: string, body: { content: string; message_type?: string; subject?: string; attachments?: Record<string, unknown>[] }) =>
    api.post<NetworkDmMessage>(`/network/messages/${memberId}`, body),
  markRead: (memberId: string) => api.post<{ updated: number }>(`/network/messages/${memberId}/read`, {}),
};

export const networkChannelApi = {
  list: (params?: { page?: number; limit?: number }) =>
    api.get<{ items: NetworkChannel[]; total: number }>(`/network/channels${qs(params)}`),
  get: (id: string) => api.get<NetworkChannel>(`/network/channels/${id}`),
  create: (body: { name: string; description?: string; channel_type?: string; visibility?: string; regions?: string[]; tags?: string[] }) =>
    api.post<NetworkChannel>('/network/channels', body),
  subscribe: (id: string) => api.post<void>(`/network/channels/${id}/subscribe`, {}),
  unsubscribe: (id: string) => api.delete(`/network/channels/${id}/subscribe`),
  posts: (id: string, params?: { page?: number; limit?: number }) =>
    api.get<{ items: ChannelPost[]; total: number }>(`/network/channels/${id}/posts${qs(params)}`),
  createPost: (id: string, body: { body: string; title?: string; post_type?: string }) =>
    api.post<ChannelPost>(`/network/channels/${id}/posts`, body),
  reply: (id: string, pid: string, body: { body: string }) =>
    api.post<ChannelPost>(`/network/channels/${id}/posts/${pid}/replies`, body),
  react: (id: string, pid: string, body: { reaction: string }) =>
    api.post<void>(`/network/channels/${id}/posts/${pid}/react`, body),
};

export const networkAllianceApi = {
  overview: (circleId: string) =>
    api.get<AllianceOverview>(`/network/alliances/${circleId}/overview`),
  contracts: (circleId: string, params?: { status?: AllianceContract['status'] }) =>
    api.get<AllianceContract[]>(`/network/alliances/${circleId}/contracts${qs(params)}`),
  entitlements: (contractId: string) =>
    api.get<AllianceContractEntitlement[]>(`/network/alliances/contracts/${contractId}/entitlements`),
  createEntitlement: (
    contractId: string,
    body: {
      member_id: string;
      entitlement_type?: AllianceContractEntitlement['entitlement_type'];
      access_level?: AllianceContractEntitlement['access_level'];
      lane_scope?: Record<string, unknown>;
      account_scope?: Record<string, unknown>;
      capacity_terms?: Record<string, unknown>;
      pricing_overlay?: Record<string, unknown>;
      status?: AllianceContractEntitlement['status'];
      effective_from?: string;
      effective_to?: string;
    },
  ) =>
    api.post<AllianceContractEntitlement>(`/network/alliances/contracts/${contractId}/entitlements`, body),
  volumeContributions: (circleId: string) =>
    api.get<AllianceVolumeContribution[]>(`/network/alliances/${circleId}/volume-contributions`),
  actionRequests: (circleId: string, params?: { status?: AllianceActionRequest['status'] }) =>
    api.get<AllianceActionRequest[]>(`/network/alliances/${circleId}/action-requests${qs(params)}`),
  decideAction: (
    actionId: string,
    body: {
      status: 'approved' | 'rejected' | 'completed' | 'cancelled';
      decision_payload?: Record<string, unknown>;
    },
  ) =>
    api.post<AllianceActionRequest>(`/network/alliances/actions/${actionId}/decision`, body),
  requestApproval: (
    actionId: string,
    body: {
      reviewer_role?: string;
      requester_role?: string;
      priority?: 'P0' | 'P1' | 'P2';
      timeout_minutes?: number;
      escalation_role?: string;
      title?: string;
      description?: string;
    },
  ) =>
    api.post<AllianceActionRequest>(`/network/alliances/actions/${actionId}/request-approval`, body),
  markAttentionRead: (actionId: string) =>
    api.post<{ marked: number }>(`/network/alliances/actions/${actionId}/mark-attention-read`, {}),
  escalateManagedOverdue: (body?: { dry_run?: boolean }) =>
    api.post<{
      escalated: number;
      action_ids: string[];
      dry_run?: boolean;
      circles: Array<{ circle_id: string; escalated: number; action_ids: string[]; dry_run?: boolean }>;
    }>(`/network/alliances/actions/escalate-overdue`, body ?? {}),
  escalateOverdue: (circleId: string, body?: { dry_run?: boolean }) =>
    api.post<{ escalated: number; action_ids: string[]; dry_run?: boolean }>(`/network/alliances/${circleId}/actions/escalate-overdue`, body ?? {}),
  createActionFromIntake: (
    circleId: string,
    intakeCaseId: string,
    body: {
      contract_id?: string;
      assigned_to_member_id?: string;
      action_type: AllianceActionRequest['action_type'];
      priority?: AllianceActionRequest['priority'];
      due_at?: string;
      subject?: string;
      summary?: string;
      requested_payload?: Record<string, unknown>;
    },
  ) =>
    api.post<AllianceActionRequest>(`/network/alliances/${circleId}/intake-cases/${intakeCaseId}/create-action`, body),
  conversationLedger: (circleId: string, params?: { source_type?: AllianceConversationLedgerEntry['source_type']; limit?: number }) =>
    api.get<AllianceConversationLedgerEntry[]>(`/network/alliances/${circleId}/conversation-ledger${qs(params)}`),
};

// ─── Helpers ────────────────────────────────────────

export const CIRCLE_TYPE_LABELS: Record<CircleType, string> = {
  ownership: '所有权集团',
  agent_network: '代理圈',
  vendor_panel: '供应商面板',
  peer_circle: '同行互助圈',
  consortium: '联营体',
};

export const CIRCLE_TYPE_COLORS: Record<CircleType, string> = {
  ownership: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  agent_network: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  vendor_panel: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  peer_circle: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  consortium: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
};

export const RFQ_STATUS_LABELS: Record<string, string> = {
  draft: '草稿',
  published: '已发布',
  quoting: '报价中',
  evaluating: '评标中',
  awarded: '已授标',
  completed: '已完成',
  cancelled: '已取消',
  expired: '已过期',
};

export const RFQ_STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  published: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  quoting: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  evaluating: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  awarded: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  cancelled: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
  expired: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500',
};
